import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import CustomSnackbar from "./SnackBar";
import useSnackbar from "./SnackbarHook";

import * as Yup from "yup";

import {
  Button as MuiButton,
  Grid,
  Link,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent as MuiCardContent,
  Divider as MuiDivider,
  Paper as MuiPaper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField as MuiTextField,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
  Tabs,
  Tab,
  Checkbox,
  FormGroup,
  Alert,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import { Box, spacing } from "@mui/system";
import {
  Delete,
  Edit,
  Search,
  CheckCircle,
  LockOpen,
  MarkEmailUnread,
  ErrorOutline,
  WarningAmber,
  Add,
} from "@mui/icons-material";
import { Tooltip, IconButton } from "@mui/material";
import { Formik } from "formik";
import {
  activateUser,
  createUser,
  deleteUser,
  fetchUsers,
  openUserForm,
  sendAllVerifications,
  sendUserVerification,
  setUser,
  updateUser,
} from "../../redux/slices/user";
import {
  fetchUserAssociation,
  updateUserAssociation,
  MembershipStatus,
} from "../../redux/slices/userAssociation";
import useAppDispatch from "../../hooks/useAppDispatch";
import useAppSelector from "../../hooks/useAppSelector";
import useAuth from "../../hooks/useAuth";
import { a11yProps } from "./CustomTabs";
import { RoleChip } from "../../components/badges/RoleChip";
import { UserPermissionsView } from "../../components/members/UserPermissionsView";
import {
  usePermission,
  usePreviewPermissions,
} from "../../hooks/usePermission";

const Card = styled(MuiCard)(spacing);

const CardContent = styled(MuiCardContent)(spacing);

const Button = styled(MuiButton)(spacing);

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Paper = styled(MuiPaper)(spacing);

const TextField = styled(MuiTextField)<{ my?: number }>(spacing);

export interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

const validationSchema = Yup.object().shape({
  firstName: Yup.string().required("Etunimi on pakollinen"),
  lastName: Yup.string().required("Sukunimi on pakollinen"),
  email: Yup.string().when("invoiceDeliveryMethod", {
    is: "EMAIL",
    then: (schema) =>
      schema
        .required(
          "Sähköposti on pakollinen, jos laskutustavaksi on valittu sähköposti"
        )
        .email("Anna oikea sähköpostiosoite"),
    otherwise: (schema) => schema.notRequired(),
  }),
  invoiceEmail: Yup.string().nullable().email("Anna oikea sähköpostiosoite"),
  street: Yup.string().required("Osoite on pakollinen"),
  postalCode: Yup.string().required("Postinumero on pakollinen"),
  city: Yup.string().required("Postitoimipaikka on pakollinen"),
  invoiceDeliveryMethod: Yup.string().oneOf(["EMAIL", "POST"]).required(),
});

function UserForm({
  associationId,
  onComplete,
}: {
  associationId: number;
  onComplete: (success: boolean) => void;
}) {
  const dispatch = useAppDispatch();
  const { currentUser } = useAuth();
  const { user } = useAppSelector((state) => state.user);
  const { currentMembership, status: membershipStatus } = useAppSelector(
    (state) => state.userAssociation
  );
  const [value, setValue] = useState(0);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<MembershipStatus>(
    MembershipStatus.ACTIVE
  );
  const [savingRoles, setSavingRoles] = useState(false);

  // Preview permissions based on selected roles
  const { permissions: previewPermissions, loading: previewLoading } =
    usePreviewPermissions(associationId, selectedRoles);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    // Fetch user association data when switching to roles tab
    if (newValue === 2 && user?.id && associationId) {
      dispatch(
        fetchUserAssociation({ userId: user.id, associationId: associationId })
      );
    }
  };

  // Update local state when membership data changes
  useEffect(() => {
    if (currentMembership) {
      setSelectedRoles(currentMembership.roles || []);
      setSelectedStatus(currentMembership.status || MembershipStatus.ACTIVE);
    }
  }, [currentMembership]);

  const handleRoleToggle = (role: string) => {
    setSelectedRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  };

  const handleStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedStatus(event.target.value as MembershipStatus);
  };

  const handleSaveRoles = async () => {
    if (!user?.id || !associationId || !currentMembership) return;

    if (selectedRoles.length === 0) {
      alert("Käyttäjällä on oltava vähintään yksi rooli");
      return;
    }

    try {
      setSavingRoles(true);
      await dispatch(
        updateUserAssociation({
          associationId,
          userId: user.id,
          updates: {
            roles: selectedRoles,
            status: selectedStatus,
          },
        })
      ).unwrap();

      // Refresh membership data
      await dispatch(
        fetchUserAssociation({ userId: user.id, associationId: associationId })
      );

      onComplete(true);
    } catch (error: any) {
      console.error("Error saving roles:", error);
      // Extract error message from backend response
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Virhe roolien tallentamisessa.";
      alert(errorMessage);
      onComplete(false);
    } finally {
      setSavingRoles(false);
    }
  };

  const handleSubmit = async (
    values: any,
    { resetForm, setErrors, setStatus, setSubmitting }: any
  ) => {
    try {
      if (user && user.id !== undefined) {
        await dispatch(
          updateUser({ id: user.id, data: values, associationId })
        ).unwrap();
      } else {
        await dispatch(createUser(values, associationId));
      }
      resetForm();
      setStatus({ sent: true });
      setSubmitting(false);
      dispatch(openUserForm(false));

      onComplete(true);
    } catch (error: any) {
      console.error("Error saving user:", error);
      setStatus({ sent: false });
      setErrors({ submit: "Error" });
      setSubmitting(false);
      onComplete(false);
    }
  };

  return (
    <Formik
      initialValues={
        user
          ? {
              firstName: user.firstName,
              lastName: user.lastName,
              phone: user.phone,
              email: user.email,
              invoiceEmail: user.invoiceEmail || "",
              street: user.address?.street,
              postalCode: user.address?.postalCode,
              city: user.address?.city,
              invoiceDeliveryMethod: user.invoiceDeliveryMethod,
            }
          : {
              firstName: "",
              lastName: "",
              email: "",
              invoiceEmail: "",
              phone: "",
              street: "",
              postalCode: "",
              city: "",
              invoiceDeliveryMethod: "EMAIL",
            }
      }
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values,
      }) => (
        <Card mb={6}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Käyttäjät
            </Typography>

            <Box sx={{ borderBottom: 1, mb: 3, borderColor: "divider" }}>
              <Tabs
                value={value}
                onChange={handleTabChange}
                aria-label="basic tabs example"
              >
                <Tab
                  label={
                    user === undefined ? "Lisää osakas" : "Muokkaa osakasta"
                  }
                  {...a11yProps(0)}
                />
                <Tab label="Näytä kiinteistöt" {...a11yProps(1)} />
                <Tab
                  label="Roolit ja oikeudet"
                  {...a11yProps(2)}
                  disabled={user === undefined}
                />
              </Tabs>
            </Box>

            {value === 0 && (
              <form onSubmit={handleSubmit}>
                <Grid container spacing={6}>
                  <Grid item md={6}>
                    <TextField
                      name="firstName"
                      label="Etunimi"
                      value={values.firstName}
                      error={Boolean(touched.firstName && errors.firstName)}
                      fullWidth
                      helperText={touched.firstName && errors.firstName}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      variant="outlined"
                      my={2}
                    />
                  </Grid>
                  <Grid item md={6}>
                    <TextField
                      name="lastName"
                      label="Sukunimi"
                      value={values.lastName}
                      error={Boolean(touched.lastName && errors.lastName)}
                      fullWidth
                      helperText={touched.lastName && errors.lastName}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      variant="outlined"
                      my={2}
                    />
                  </Grid>
                  <Grid item md={6}>
                    <TextField
                      name="phone"
                      label="Puhelinnumero"
                      value={values.phone}
                      error={Boolean(touched.phone && errors.phone)}
                      fullWidth
                      helperText={touched.phone && errors.phone}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      //type="email"
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item md={6}>
                    <TextField
                      name="email"
                      label="Sähköposti"
                      value={values.email}
                      error={Boolean(touched.email && errors.email)}
                      fullWidth
                      helperText={touched.email && errors.email}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      type="email"
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item md={6}>
                    <TextField
                      name="invoiceEmail"
                      label="Laskutussähköposti"
                      placeholder="esim. naapuri@gmail.com"
                      value={values.invoiceEmail}
                      error={Boolean(
                        touched.invoiceEmail && errors.invoiceEmail
                      )}
                      fullWidth
                      helperText={
                        (touched.invoiceEmail && errors.invoiceEmail) ||
                        "Jos asetettu, laskut ja kokouskutsut lähetetään tähän osoitteeseen"
                      }
                      onBlur={handleBlur}
                      onChange={handleChange}
                      type="email"
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item md={6}>
                    <TextField
                      name="street"
                      label="Osoite"
                      value={values.street}
                      error={Boolean(touched.street && errors.street)}
                      fullWidth
                      helperText={touched.street && errors.street}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item md={6}>
                    <TextField
                      name="postalCode"
                      label="Postinumero"
                      value={values.postalCode}
                      error={Boolean(touched.postalCode && errors.postalCode)}
                      fullWidth
                      helperText={touched.postalCode && errors.postalCode}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item md={6}>
                    <TextField
                      name="city"
                      label="Postitoimipaikka"
                      value={values.city}
                      error={Boolean(touched.city && errors.city)}
                      fullWidth
                      helperText={touched.city && errors.city}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item md={6}>
                    <FormControl component="fieldset">
                      <FormLabel component="legend">Laskutustapa</FormLabel>
                      <RadioGroup
                        name="invoiceDeliveryMethod"
                        value={values.invoiceDeliveryMethod}
                        onChange={handleChange}
                        row
                      >
                        <FormControlLabel
                          value="EMAIL"
                          control={<Radio />}
                          label="Sähköposti"
                        />
                        <FormControlLabel
                          value="POST"
                          control={<Radio />}
                          label="Postiosoite"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                </Grid>

                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  mt={3}
                  mr={2}
                >
                  Tallenna
                </Button>
                <Button
                  variant="outlined"
                  color="inherit"
                  mt={3}
                  onClick={() => dispatch(openUserForm(false))}
                >
                  Peruuta
                </Button>
              </form>
            )}
            {value === 1 &&
              (user !== undefined ? (
                <>
                  <Typography variant="h6" gutterBottom mt={3} mb={3}>
                    Kiinteistöt
                  </Typography>
                  {(user?.properties || []).length > 0 ? (
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Nimi</TableCell>
                          <TableCell>Tunnus</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {user.properties.map((p: any) => (
                          <TableRow key={p.code}>
                            <TableCell>{p.name}</TableCell>
                            <TableCell>{p.code}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <Typography variant="body1" color="textSecondary" mt={2}>
                      Osakkaalla ei ole kiinteistöjä.
                    </Typography>
                  )}
                </>
              ) : (
                <Typography variant="body1" color="textSecondary" mt={2}>
                  Osakkaalla ei ole kiinteistöjä.
                </Typography>
              ))}

            {value === 2 && user !== undefined && (
              <>
                <Typography variant="h6" gutterBottom mt={3} mb={3}>
                  Roolit ja oikeudet tiekunnassa
                </Typography>

                {membershipStatus === "loading" ? (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    py={4}
                  >
                    <CircularProgress />
                  </Box>
                ) : currentMembership ? (
                  <>
                    {user.isSuperAdmin ? (
                      <Alert severity="warning" sx={{ mb: 3 }}>
                        <strong>
                          {user.firstName} {user.lastName}
                        </strong>{" "}
                        on pääkäyttäjä (SuperAdmin). Pääkäyttäjän rooleja ja
                        oikeuksia ei voi muokata.
                      </Alert>
                    ) : (
                      <Alert severity="info" sx={{ mb: 3 }}>
                        Muokkaa käyttäjän{" "}
                        <strong>
                          {user.firstName} {user.lastName}
                        </strong>{" "}
                        rooleja ja jäsenyyden tilaa.
                      </Alert>
                    )}

                    <Grid container spacing={4}>
                      {/* Roles Section */}
                      <Grid item xs={12} md={6}>
                        <FormControl component="fieldset" fullWidth>
                          <FormLabel component="legend" sx={{ mb: 2 }}>
                            Roolit
                          </FormLabel>
                          <FormGroup>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={selectedRoles.includes("ADMIN")}
                                  onChange={() => handleRoleToggle("ADMIN")}
                                  disabled={user.isSuperAdmin}
                                />
                              }
                              label="Ylläpitäjä (ADMIN)"
                            />
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={selectedRoles.includes("ACCOUNTANT")}
                                  onChange={() =>
                                    handleRoleToggle("ACCOUNTANT")
                                  }
                                  disabled={user.isSuperAdmin}
                                />
                              }
                              label="Kirjanpitäjä (ACCOUNTANT)"
                            />
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={selectedRoles.includes("MEMBER")}
                                  onChange={() => handleRoleToggle("MEMBER")}
                                  disabled={user.isSuperAdmin}
                                />
                              }
                              label="Osakas (MEMBER)"
                            />
                            {currentUser?.isSuperAdmin && (
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={selectedRoles.includes(
                                      "CONTRACTOR"
                                    )}
                                    onChange={() =>
                                      handleRoleToggle("CONTRACTOR")
                                    }
                                    disabled={user.isSuperAdmin}
                                  />
                                }
                                label="Urakoitsija (CONTRACTOR)"
                              />
                            )}
                            {currentUser?.isSuperAdmin && (
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={selectedRoles.includes(
                                      "GOVERNMENT"
                                    )}
                                    onChange={() =>
                                      handleRoleToggle("GOVERNMENT")
                                    }
                                    disabled={user.isSuperAdmin}
                                  />
                                }
                                label="Viranomainen (GOVERNMENT)"
                              />
                            )}
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={selectedRoles.includes("GUEST")}
                                  onChange={() => handleRoleToggle("GUEST")}
                                  disabled={user.isSuperAdmin}
                                />
                              }
                              label="Vieras (GUEST)"
                            />
                          </FormGroup>
                          {selectedRoles.length === 0 && (
                            <Typography
                              variant="caption"
                              color="error"
                              sx={{ mt: 1 }}
                            >
                              Vähintään yksi rooli on valittava
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>

                      {/* Status Section */}
                      <Grid item xs={12} md={6}>
                        <FormControl component="fieldset" fullWidth>
                          <FormLabel component="legend" sx={{ mb: 2 }}>
                            Jäsenyyden tila
                          </FormLabel>
                          <RadioGroup
                            value={selectedStatus}
                            onChange={handleStatusChange}
                          >
                            <FormControlLabel
                              value={MembershipStatus.ACTIVE}
                              control={<Radio />}
                              label="Aktiivinen"
                              disabled={user.isSuperAdmin}
                            />
                            <FormControlLabel
                              value={MembershipStatus.INACTIVE}
                              control={<Radio />}
                              label="Ei aktiivinen"
                              disabled={user.isSuperAdmin}
                            />
                            <FormControlLabel
                              value={MembershipStatus.SUSPENDED}
                              control={<Radio />}
                              label="Jäädytetty"
                              disabled={user.isSuperAdmin}
                            />
                          </RadioGroup>
                        </FormControl>

                        {/* Current Roles Display */}
                        <Box sx={{ mt: 3 }}>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            mb={1}
                          >
                            Valitut roolit:
                          </Typography>
                          <Box
                            sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}
                          >
                            {selectedRoles.length > 0 ? (
                              selectedRoles.map((role: string) => (
                                <RoleChip key={role} role={role} size="small" />
                              ))
                            ) : (
                              <Typography
                                variant="body2"
                                color="textSecondary"
                                fontStyle="italic"
                              >
                                Ei roolia valittu
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>

                    <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSaveRoles}
                        disabled={
                          savingRoles ||
                          selectedRoles.length === 0 ||
                          user.isSuperAdmin
                        }
                      >
                        {savingRoles ? (
                          <CircularProgress size={24} />
                        ) : (
                          "Tallenna muutokset"
                        )}
                      </Button>
                      <Button
                        variant="outlined"
                        color="inherit"
                        onClick={() => {
                          // Reset to original values
                          if (currentMembership) {
                            setSelectedRoles(currentMembership.roles || []);
                            setSelectedStatus(
                              currentMembership.status ||
                                MembershipStatus.ACTIVE
                            );
                          }
                        }}
                        disabled={savingRoles}
                      >
                        Peruuta muutokset
                      </Button>
                    </Box>

                    {/* Permissions Matrix */}
                    <Box sx={{ mt: 4 }}>
                      <Typography variant="h6" gutterBottom>
                        Käyttöoikeudet (esikatselu valituilla rooleilla)
                      </Typography>
                      {user.isSuperAdmin ? (
                        <Alert severity="info">
                          Pääkäyttäjän (SuperAdmin) oikeuksia ei voi tarkastella
                          tai muokata.
                        </Alert>
                      ) : (
                        <>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{ mb: 2 }}
                          >
                            Oikeudet päivittyvät automaattisesti kun rooleja
                            muutetaan. Muista tallentaa muutokset.
                          </Typography>
                          <UserPermissionsView
                            associationId={associationId}
                            permissions={previewPermissions}
                            isLoading={previewLoading}
                          />
                        </>
                      )}
                    </Box>

                    {/* SuperAdmin: Custom Permission Overrides */}
                    {currentUser?.isSuperAdmin && !user.isSuperAdmin && (
                      <Box sx={{ mt: 4 }}>
                        <Divider sx={{ my: 3 }} />
                        <Typography variant="h6" gutterBottom>
                          Käyttöoikeuksien ohitukset (SuperAdmin)
                        </Typography>
                        <Alert severity="warning" sx={{ mb: 2 }}>
                          Voit lisätä tai poistaa yksittäisiä käyttöoikeuksia
                          klikkaamalla soluja alla. GRANT lisää oikeuden roolien
                          ulkopuolelta, DENY estää roolin antaman oikeuden.
                        </Alert>
                        <UserPermissionsView
                          associationId={associationId}
                          userId={user.id}
                          editable={true}
                          onPermissionChange={() => {
                            // Refresh membership data after permission change
                            if (associationId && user?.id) {
                              dispatch(
                                fetchUserAssociation({
                                  userId: user.id,
                                  associationId: associationId as number,
                                })
                              );
                            }
                          }}
                        />
                      </Box>
                    )}
                  </>
                ) : (
                  <Typography variant="body1" color="textSecondary" mt={2}>
                    Käyttäjä ei ole jäsen tässä tiekunnassa.
                  </Typography>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}
    </Formik>
  );
}

const isRealEmail = (email: string) => !email.endsWith("@tiehoitokunta.fi");

function UserList(props: any) {
  const dispatch = useAppDispatch();

  const { status = "idle" } = useAppSelector((state) => state.user || {});

  const { hasPermission: canDeleteUser } = usePermission(
    "user",
    "delete",
    props.associationId
  );
  const { hasPermission: canUpdateUser } = usePermission(
    "user",
    "update",
    props.associationId
  );

  const eligibleForBulkSend = (props.rows || []).filter(
    (u: any) => !u.isVerified && isRealEmail(u.email)
  ).length;

  useEffect(() => {
    if (props.associationId) {
      dispatch(fetchUsers({ associationId: props.associationId }));
    }
  }, [dispatch, props.associationId]);

  const [sortField, setSortField] = useState<
    "firstName" | "lastName" | "email"
  >("firstName");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [roleFilter, setRoleFilter] = useState<string>("ALL");

  const handleSort = (field: "firstName" | "lastName" | "email") => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortArrow = (field: "firstName" | "lastName" | "email") => {
    if (sortField !== field) return "";
    return sortDirection === "asc" ? " ↑" : " ↓";
  };

  // Filter rows by role
  const filteredRows = [...(props.rows || [])].filter((row) => {
    if (roleFilter === "ALL") return true;
    return row.roles?.includes(roleFilter);
  });

  const sortedRows = filteredRows.sort((a, b) => {
    const aVal = a[sortField]?.toLowerCase?.() || "";
    const bVal = b[sortField]?.toLowerCase?.() || "";
    if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
    if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  return (
    <Card mb={6}>
      <CardContent
        pb={1}
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" gutterBottom>
          Käyttäjät
        </Typography>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="role-filter-label">
              Suodata roolin mukaan
            </InputLabel>
            <Select
              labelId="role-filter-label"
              value={roleFilter}
              label="Suodata roolin mukaan"
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <MenuItem value="ALL">Kaikki</MenuItem>
              <MenuItem value="ADMIN">Ylläpitäjä</MenuItem>
              <MenuItem value="ACCOUNTANT">Kirjanpitäjä</MenuItem>
              <MenuItem value="MEMBER">Osakas</MenuItem>
              <MenuItem value="CONTRACTOR">Urakoitsija</MenuItem>
              <MenuItem value="GOVERNMENT">Viranomainen</MenuItem>
              <MenuItem value="GUEST">Vieras</MenuItem>
            </Select>
          </FormControl>
          {canUpdateUser && eligibleForBulkSend > 0 && (
            <Tooltip
              title={`Lähetä aktivointilinkki ${eligibleForBulkSend} aktivoimattomalle käyttäjälle, joilla on sähköpostiosoite`}
            >
              <Button
                variant="outlined"
                color="warning"
                size="small"
                startIcon={<MarkEmailUnread fontSize="small" />}
                onClick={() => props.handleSendAll(eligibleForBulkSend)}
              >
                Lähetä kaikille ({eligibleForBulkSend})
              </Button>
            </Tooltip>
          )}
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<Add />}
            onClick={() => props.handleEditUser(undefined, true)}
          >
            Lisää käyttäjä
          </Button>
        </Box>
      </CardContent>

      <Paper>
        <Table size={"small"}>
          <TableHead>
            <TableRow>
              <TableCell
                onClick={() => handleSort("firstName")}
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                Etunimi{getSortArrow("firstName")}
              </TableCell>
              <TableCell
                onClick={() => handleSort("lastName")}
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                Sukunimi{getSortArrow("lastName")}
              </TableCell>
              <TableCell
                onClick={() => handleSort("email")}
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                Sähköposti{getSortArrow("email")}
              </TableCell>
              <TableCell>Tila</TableCell>
              <TableCell>Roolit</TableCell>
              <TableCell>Toiminnot</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {status === "loading" ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="50vh"
                  >
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : sortedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Ei käyttäjiä
                </TableCell>
              </TableRow>
            ) : (
              sortedRows.map((row: any) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.firstName}</TableCell>
                  <TableCell>{row.lastName}</TableCell>
                  <TableCell>
                    {isRealEmail(row.email) ? row.email : "(ei sähköpostia)"}
                  </TableCell>
                  <TableCell>
                    {row.isVerified ? (
                      <Tooltip title="Tunnus aktivoitu">
                        <CheckCircle
                          sx={{ fontSize: 18, color: "success.main" }}
                        />
                      </Tooltip>
                    ) : isRealEmail(row.email) ? (
                      <Tooltip title="Odottaa sähköpostivahvistusta">
                        <WarningAmber
                          sx={{ fontSize: 18, color: "warning.main" }}
                        />
                      </Tooltip>
                    ) : (
                      <Tooltip title="Ei sähköpostiosoitetta — aktivoi manuaalisesti tai QR-koodi seuraavassa laskussa">
                        <ErrorOutline
                          sx={{ fontSize: 18, color: "error.main" }}
                        />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                      {row.roles?.map((role: string) => (
                        <RoleChip key={role} role={role} size="small" />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Näytä tiedot">
                      <IconButton
                        size="small"
                        onClick={() => props.handleShowUser(row, true)}
                      >
                        <Search fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Muokkaa">
                      <IconButton
                        size="small"
                        onClick={() => props.handleEditUser(row, true)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {canUpdateUser && !row.isVerified && (
                      <>
                        <Tooltip title="Aktivoi tunnus manuaalisesti">
                          <IconButton
                            size="small"
                            onClick={() => props.handleActivate(row)}
                          >
                            <LockOpen fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {isRealEmail(row.email) && (
                          <Tooltip title="Lähetä aktivointilinkki sähköpostilla">
                            <IconButton
                              size="small"
                              onClick={() => props.handleSendVerification(row)}
                            >
                              <MarkEmailUnread fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    )}
                    {canDeleteUser && (
                      <Tooltip title="Poista">
                        <IconButton
                          size="small"
                          onClick={() => props.handleDelete(row)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
    </Card>
  );
}

function Users() {
  const { currentUser } = useAuth();
  const dispatch = useAppDispatch();
  const { users, open } = useAppSelector((state) => state.user);

  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  const {
    openSnackbar,
    snackbarMessage,
    snackbarSeverity,
    progress,
    setOpenSnackbar,
    setSnackbarMessage,
    setSnackbarSeverity,
    setProgress,
    duration,
  } = useSnackbar();

  const handleShowUser = (user: any, isOpen: boolean) => {
    dispatch(setUser(user));
    dispatch(openUserForm(isOpen));
  };

  const handleEditUser = (user: User | undefined, isOpen: boolean) => {
    dispatch(setUser(user));
    dispatch(openUserForm(isOpen));
  };

  const handleDelete = async (user: User) => {
    const confirmed = window.confirm(
      `Haluatko varmasti poistaa "${user.firstName + " " + user.lastName}"?`
    );
    if (confirmed) {
      setProgress(100);
      try {
        await dispatch(deleteUser(user.id, associationId));
        setSnackbarMessage("Osakas poistettu onnistuneesti!");
        setSnackbarSeverity("success");
        setOpenSnackbar(true);
      } catch (e) {
        setSnackbarMessage("Virhe poistettaessa osakasta.");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      }
    }
  };

  const handleActivate = async (user: any) => {
    const confirmed = window.confirm(
      `Aktivoidaanko "${user.firstName} ${user.lastName}" ilman sähköpostivahvistusta? Käyttäjä voi kirjautua sisään välittömästi.`
    );
    if (!confirmed) return;
    const result = await dispatch(
      activateUser({ userId: user.id, associationId })
    );
    if (activateUser.fulfilled.match(result)) {
      setSnackbarMessage("Tunnus aktivoitu onnistuneesti!");
      setSnackbarSeverity("success");
    } else {
      setSnackbarMessage((result.payload as string) || "Virhe aktivoinnissa.");
      setSnackbarSeverity("error");
    }
    setOpenSnackbar(true);
  };

  const handleSendAll = async (count: number) => {
    const confirmed = window.confirm(
      `Lähetetäänkö aktivointilinkki ${count} käyttäjälle? Käyttäjät, joille linkki on jo lähetetty viimeisen 24 tunnin aikana, ohitetaan.`
    );
    if (!confirmed) return;
    const result = await dispatch(sendAllVerifications(associationId));
    if (sendAllVerifications.fulfilled.match(result)) {
      const { sent, skipped } = result.payload;
      setSnackbarMessage(
        `Aktivointilinkki lähetetty ${sent} käyttäjälle. Ohitettu: ${skipped}.`
      );
      setSnackbarSeverity("success");
    } else {
      setSnackbarMessage(
        (result.payload as string) || "Virhe linkkien lähetyksessä."
      );
      setSnackbarSeverity("error");
    }
    setOpenSnackbar(true);
  };

  const handleSendVerification = async (user: any) => {
    const result = await dispatch(
      sendUserVerification({ userId: user.id, associationId })
    );
    if (sendUserVerification.fulfilled.match(result)) {
      setSnackbarMessage("Aktivointilinkki lähetetty!");
      setSnackbarSeverity("success");
    } else {
      setSnackbarMessage(
        (result.payload as string) || "Virhe linkin lähetyksessä."
      );
      setSnackbarSeverity("error");
    }
    setOpenSnackbar(true);
  };

  const handleCloseSnackbar = () => setOpenSnackbar(false);

  useEffect(() => {
    dispatch(fetchUsers({ associationId }));

    return () => {
      dispatch(openUserForm(false));
    };
  }, [dispatch, associationId]);

  return (
    <React.Fragment>
      <Helmet title="Käyttäjät" />
      <Typography variant="h3" gutterBottom display="inline">
        Käyttäjät
      </Typography>
      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} to="/">
          Etusivu
        </Link>
        <Typography>Käyttäjät</Typography>
      </Breadcrumbs>
      <Divider my={6} />
      {open ? (
        <UserForm
          associationId={associationId}
          onComplete={(success: any) => {
            setProgress(100);
            if (success) {
              setSnackbarMessage("Osakas tallennettu onnistuneesti!");
              setSnackbarSeverity("success");
            } else {
              setSnackbarMessage("Virhe tallennettaessa osakasta.");
              setSnackbarSeverity("error");
            }
            setOpenSnackbar(true);
          }}
        />
      ) : (
        <UserList
          rows={users}
          handleShowUser={handleShowUser}
          handleEditUser={handleEditUser}
          handleDelete={handleDelete}
          handleActivate={handleActivate}
          handleSendVerification={handleSendVerification}
          handleSendAll={handleSendAll}
          openForm={open}
          associationId={associationId}
        />
      )}
      <CustomSnackbar
        open={openSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity}
        progress={progress}
        duration={duration}
        onClose={handleCloseSnackbar}
      />
    </React.Fragment>
  );
}

export default Users;
